import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle2, XCircle, Loader2, ArrowRight, MessageCircle, FileDown } from 'lucide-react';
import { CONTACT } from '../mock';
import fbTrack from '../lib/fbpixel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const orderId = params.get('order_id');
  const [state, setState] = useState('checking');
  const [booking, setBooking] = useState(null);
  const purchaseFiredRef = React.useRef(false);

  useEffect(() => {
    let cancelled = false;
    if (!orderId) { setState('error'); return; }
    const check = async (attempt = 0) => {
      try {
        const { data } = await axios.post(`${API}/bookings/${orderId}/verify`);
        if (cancelled) return;
        setBooking(data);
        if (data.payment_status === 'PAID') {
          setState('paid');
          if (!purchaseFiredRef.current) {
            purchaseFiredRef.current = true;
            fbTrack.purchase({
              id: data.id,
              name: data.name,
              mobile: data.mobile,
              package_id: data.package_id,
              package_name: data.package_name,
              amount: data.amount,
            });
          }
        }
        else if (data.payment_status === 'FAILED') setState('failed');
        else if (attempt < 4) setTimeout(() => check(attempt + 1), 2000);
        else setState('pending');
      } catch {
        if (!cancelled) setState('error');
      }
    };
    check();
    return () => { cancelled = true; };
  }, [orderId]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="card-dark w-full max-w-lg p-8 text-center">
        {state === 'checking' && (
          <>
            <Loader2 className="w-14 h-14 text-pink-400 mx-auto animate-spin" />
            <h1 className="mt-5 text-2xl font-extrabold text-white">પેમેન્ટ verify થઈ રહ્યું છે...</h1>
            <p className="mt-2 text-gray-400 text-sm">Cashfree પરથી confirmation લઈ રહ્યા છીએ</p>
          </>
        )}
        {state === 'paid' && (
          <>
            <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center" style={{background:'linear-gradient(135deg,#a855f7,#ec4899)'}}>
              <CheckCircle2 className="w-9 h-9 text-white" />
            </div>
            <h1 className="mt-5 text-3xl font-extrabold gradient-text">બુકિંગ કન્ફર્મ!</h1>
            <p className="mt-2 text-gray-300">Booking ID: <span className="font-mono text-white">{booking?.id}</span></p>
            <div className="mt-5 rounded-xl border border-purple-900/60 bg-[#14141c] p-4 text-sm text-gray-300 text-left space-y-1.5">
              <div className="flex justify-between"><span className="text-gray-400">પેકેજ</span><span className="text-white">{booking?.package_name}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">તારીખ</span><span className="text-white">{new Date(booking?.booking_date).toLocaleDateString('en-IN')}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Amount Paid</span><span className="text-white font-bold">₹{booking?.amount}</span></div>
            </div>
            <p className="mt-4 text-sm text-gray-400">કન્ફર્મેશન WhatsApp પર મળી ગયું હશે. અમારી ટીમ ટૂંક સમયમાં સંપર્ક કરશે.</p>
            {booking?.doc_number && (
              <a
                href={`${API}/bookings/${booking.id}/document.pdf`}
                target="_blank" rel="noreferrer"
                className="mt-5 inline-flex items-center justify-center gap-2 w-full h-12 rounded-full font-semibold text-white"
                style={{background:'linear-gradient(90deg,#a855f7,#ec4899)'}}
              >
                <FileDown className="w-5 h-5" />
                {booking.doc_type === 'INVOICE' ? 'Tax Invoice ડાઉનલોડ કરો' : 'Payment Receipt ડાઉનલોડ કરો'}
                <span className="text-xs opacity-80 ml-2">{booking.doc_number}</span>
              </a>
            )}
            <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={() => navigate('/')} variant="outline" className="rounded-full h-11 px-6 border-purple-800/60 text-gray-200 hover:bg-purple-950/30">Home <ArrowRight className="w-4 h-4 ml-1" /></Button>
              <a href={`https://wa.me/${CONTACT.whatsapp}`} className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-full border border-purple-800/60 text-gray-200 hover:border-pink-500/70"><MessageCircle className="w-4 h-4 text-green-400" /> WhatsApp</a>
            </div>
          </>
        )}
        {state === 'pending' && (
          <>
            <Loader2 className="w-14 h-14 text-yellow-400 mx-auto animate-spin" />
            <h1 className="mt-5 text-2xl font-extrabold text-white">પેમેન્ટ પ્રોસેસ થઈ રહ્યું છે</h1>
            <p className="mt-2 text-gray-400 text-sm">થોડી વારમાં અપડેટ મળશે. તમારું Booking ID: <span className="font-mono text-white">{booking?.id}</span></p>
            <Button onClick={() => window.location.reload()} className="gradient-btn rounded-full h-11 px-6 font-semibold mt-6">ફરીથી ચેક કરો</Button>
          </>
        )}
        {(state === 'failed' || state === 'error') && (
          <>
            <XCircle className="w-14 h-14 text-red-400 mx-auto" />
            <h1 className="mt-5 text-2xl font-extrabold text-white">પેમેન્ટ નિષ્ફળ</h1>
            <p className="mt-2 text-gray-400 text-sm">કૃપા કરી ફરી પ્રયાસ કરો અથવા +91 84606 92482 પર કૉલ કરો</p>
            <Button onClick={() => navigate('/')} className="gradient-btn rounded-full h-11 px-6 font-semibold mt-6">Home</Button>
          </>
        )}
      </Card>
    </div>
  );
}
